/**
 * Smart Spender rules shared by the cart, the minicart, the dashboard card and
 * the route modules. Pure and client-safe.
 *
 * SFCC-12281: the line-item predicate that gates the Smart Spender cart notice,
 * shared by the cart page and the minicart so the two can't drift. The
 * ProductLineItem attribute (`isSmartSpenderProduct`, boolean, default false —
 * shipped via migration) is set by the FSA cart-build job on the line items it
 * adds.
 *
 * COM-468: landing-page deep links into the dashboard modal: `?openEnroll=true`
 * (SFRA `easySpenderModal.js:44-47`) and the Workers-only
 * `?openUpdateCategories=true` the Builder landing page links an enrolled
 * shopper to.
 */
import type { ShouldRevalidateFunction } from "react-router";

import type { SmartSpenderDeepLink, SmartSpenderLineItem } from "./types";

export function hasSmartSpenderProduct(
  productItems: readonly SmartSpenderLineItem[] | undefined,
): boolean {
  return (
    productItems?.some(item => item.c_isSmartSpenderProduct === true) ?? false
  );
}

/** The enrol / update-categories / unenrol action (`routes/resources/abs-consent.ts`). */
export const ABS_CONSENT_ACTION = "/resources/abs-consent";

export const OPEN_ENROLL_PARAM = "openEnroll";
export const OPEN_UPDATE_CATEGORIES_PARAM = "openUpdateCategories";
const DEEP_LINK_PARAMS = [OPEN_ENROLL_PARAM, OPEN_UPDATE_CATEGORIES_PARAM];

const hasDeepLink = (params: URLSearchParams) =>
  DEEP_LINK_PARAMS.some(p => params.has(p));

/**
 * Which screen a deep link opens and whether to strip it.
 *
 * - Only the link matching the shopper's enrolment opens anything: `openEnroll`
 *   for a not-enrolled shopper, `openUpdateCategories` for an enrolled one.
 * - `consentStatus === null` (no card: ineligible, or another site) opens
 *   nothing, like SFRA's silent OpenModal no-op.
 * - Every deep-link param is stripped whenever one is present, matched or not
 *   and card or not (SFRA strips `openEnroll` unconditionally), so a stale link
 *   cannot fire after enrolment changes.
 */
export function resolveSmartSpenderDeepLink(
  params: URLSearchParams,
  consentStatus: boolean | null,
): SmartSpenderDeepLink {
  if (!hasDeepLink(params)) return { screen: null, strip: false };
  if (consentStatus === null) return { screen: null, strip: true };
  const param = consentStatus
    ? OPEN_UPDATE_CATEGORIES_PARAM
    : OPEN_ENROLL_PARAM;
  if (params.get(param) !== "true") return { screen: null, strip: true };
  return {
    screen: consentStatus ? "updateCategories" : "agreement",
    strip: true,
  };
}

/** Removes every deep-link param, in place; returns `params` for chaining. */
export function stripSmartSpenderDeepLinks(
  params: URLSearchParams,
): URLSearchParams {
  DEEP_LINK_PARAMS.forEach(p => params.delete(p));
  return params;
}

/**
 * True when `current` → `next` only removes deep-link params: the card's own
 * strip. The `/my-account` routes skip revalidation for it, so a cosmetic URL
 * change does not re-run the dashboard loaders.
 */
export function isSmartSpenderDeepLinkStrip(current: URL, next: URL): boolean {
  if (current.origin !== next.origin || current.pathname !== next.pathname) {
    return false;
  }
  if (!hasDeepLink(current.searchParams)) return false;
  const stripped = stripSmartSpenderDeepLinks(
    new URLSearchParams(current.searchParams),
  );
  return stripped.toString() === next.searchParams.toString();
}

/**
 * `shouldRevalidate` for the `/my-account` layout and dashboard: RR7's default,
 * except no re-run for the card's deep-link strip (a GET navigation).
 */
export const revalidateUnlessDeepLinkStrip: ShouldRevalidateFunction = ({
  formMethod,
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}) =>
  !formMethod && isSmartSpenderDeepLinkStrip(currentUrl, nextUrl)
    ? false
    : defaultShouldRevalidate;
